import { FormBlockInstance, LocalizedText } from '../api-types';

export const MULTI_SELECT_TYPE = 'multi_select';
export const MULTI_SELECT_TYPE_VERSION = 1;

/** Falls back to the server's own default so the field is never unbounded if config is thin. */
export const DEFAULT_OTHER_TEXT_MAX_LENGTH = 200;

export type MultiSelectOptionKind = 'CHOICE' | 'OTHER' | 'NONE';

export interface MultiSelectOption {
  key: string;
  kind: MultiSelectOptionKind;
  label: LocalizedText | null;
  description: LocalizedText | null;
}

/**
 * The one rule type `multi_select@1` understands: how many selections are *useful*, as opposed to
 * how many are *allowed*. It never blocks — see `MultiSelectConfig`.
 */
export interface MultiSelectRecommendation {
  min: number;
  max: number;
  message: LocalizedText | null;
}

export interface MultiSelectConfig {
  options: MultiSelectOption[];
  /**
   * Hard limits. The server refuses answers outside them, so the renderer must too — anything else
   * walks the respondent into a submit that fails.
   */
  minSelections: number | null;
  maxSelections: number | null;
  /**
   * Advice, deliberately a separate field from the limits above. Being outside it is a completely
   * valid answer, and treating it as anything else turns a recommendation into a hidden rule.
   */
  recommendation: MultiSelectRecommendation | null;
  otherTextMaxLength: number;
  showDescriptions: boolean;
}

/** The answer shape pro-core's `multi_select@1` validates. */
export interface MultiSelectAnswer {
  optionKeys: string[];
  otherText?: string | null;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asLocalizedText = (value: unknown): LocalizedText | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );
  return entries.length ? Object.fromEntries(entries) : null;
};

const asKind = (value: unknown): MultiSelectOptionKind =>
  value === 'OTHER' || value === 'NONE' ? value : 'CHOICE';

const asPositiveInt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : null;

const readRecommendation = (
  rules: unknown
): MultiSelectRecommendation | null => {
  if (!Array.isArray(rules)) {
    return null;
  }
  const rule = rules
    .map(asRecord)
    .find(entry => entry.type === 'RECOMMENDED_SELECTION_COUNT');
  if (!rule) {
    return null;
  }
  const min = asPositiveInt(rule.min);
  const max = asPositiveInt(rule.max);
  // A half range would render as guidance nobody can act on, so it is treated as no guidance.
  return min !== null && max !== null && min <= max
    ? { min, max, message: asLocalizedText(rule.message) }
    : null;
};

/**
 * Read a stored config into the shape this renderer needs.
 *
 * Tolerant on purpose. The definition was normalized and validated by pro-core before it was ever
 * stored, so the fields are expected to be there — but a renderer that throws on a surprise takes
 * the whole form down for the respondent, and a question rendered with fewer options than intended
 * is still refused at submit. Being forgiving here costs nothing and removes a crash class.
 */
export const readMultiSelectConfig = (
  block: FormBlockInstance
): MultiSelectConfig => {
  const config = asRecord(block.config);
  const data = asRecord(config.data);
  const interaction = asRecord(config.interaction);
  const presentation = asRecord(config.presentation);
  const rawOptions = Array.isArray(data.options) ? data.options : [];

  const options: MultiSelectOption[] = rawOptions
    .map(asRecord)
    .filter(option => typeof option.key === 'string' && option.key.length > 0)
    .map(option => ({
      key: option.key as string,
      kind: asKind(option.kind),
      label: asLocalizedText(option.label),
      description: asLocalizedText(option.description)
    }));

  return {
    options,
    minSelections: asPositiveInt(interaction.minSelections),
    maxSelections: asPositiveInt(interaction.maxSelections),
    recommendation: readRecommendation(config.rules),
    otherTextMaxLength:
      asPositiveInt(interaction.otherTextMaxLength) ??
      DEFAULT_OTHER_TEXT_MAX_LENGTH,
    showDescriptions: presentation.showDescriptions === true
  };
};

export const readMultiSelectAnswer = (
  answer: unknown
): MultiSelectAnswer | null => {
  const record = asRecord(answer);
  if (!Array.isArray(record.optionKeys)) {
    return null;
  }
  return {
    optionKeys: record.optionKeys.filter(
      (key): key is string => typeof key === 'string' && key.length > 0
    ),
    otherText: typeof record.otherText === 'string' ? record.otherText : null
  };
};

export const isExclusive = (option: MultiSelectOption): boolean =>
  option.kind === 'NONE';

/**
 * Apply a tap to the current selection, mirroring what the server will accept.
 *
 * The exclusivity is enforced here rather than left to the respondent to work out: choosing "none of
 * these" clears everything, and choosing anything else clears "none of these". Rendering both as
 * selected for even a moment would show a state the server refuses.
 */
export const toggleSelection = (
  selected: string[],
  option: MultiSelectOption,
  options: MultiSelectOption[]
): string[] => {
  if (selected.includes(option.key)) {
    return selected.filter(key => key !== option.key);
  }
  if (isExclusive(option)) {
    return [option.key];
  }
  const exclusiveKeys = options.filter(isExclusive).map(entry => entry.key);
  return [...selected.filter(key => !exclusiveKeys.includes(key)), option.key];
};

/**
 * Whether an answer is complete enough to move on.
 *
 * Deliberately mirrors the server's rules and nothing more. The recommendation is not consulted:
 * being outside it is valid, so gating Next on it would silently convert advice into a requirement —
 * exactly the confusion the two separate config fields exist to prevent.
 */
export const isMultiSelectAnswered = (
  answer: unknown,
  config: MultiSelectConfig
): boolean => {
  const parsed = readMultiSelectAnswer(answer);
  if (!parsed || parsed.optionKeys.length === 0) {
    return false;
  }
  const chosen = config.options.filter(option =>
    parsed.optionKeys.includes(option.key)
  );
  if (chosen.some(option => option.kind === 'OTHER')) {
    if (!parsed.otherText || parsed.otherText.trim().length === 0) {
      return false;
    }
  }
  // "None of these" is a complete answer in itself, so the count limits do not apply to it.
  if (chosen.some(isExclusive)) {
    return chosen.length === 1;
  }
  const { minSelections, maxSelections } = config;
  if (minSelections !== null && chosen.length < minSelections) {
    return false;
  }
  return maxSelections === null || chosen.length <= maxSelections;
};
